import { Link, useParams } from 'react-router-dom';
import type { ComprobanteDetalle, RenglonIva } from '../api';
import { BotonesDePdf } from './BotonesDePdf';
import { useComprobante } from './consultas';
import {
  etiquetaDeCondicionIva,
  etiquetaDeConcepto,
  fechaArgentina,
  formateaMoneda,
  nombreDeComprobante,
  numeroFormateado,
  receptorLegible,
} from './dominio';
import { mensajeDeConsulta } from './mensajes';

/**
 * Un comprobante emitido, completo (KAN-146). Ruta `/comprobantes/:id`.
 *
 * # QUÉ AGREGA SOBRE LA FILA DEL LISTADO
 *
 * El **desglose de IVA por alícuota** (`facturas_iva`, migración V4), que el
 * listado no trae. No es un adorno: de un `imp_iva` agregado **no se puede
 * reconstruir** un comprobante con 21% y 10,5% mezclados, y esa mezcla es
 * exactamente lo que hay que poder mirar cuando alguien discute un importe
 * contra el papel.
 *
 * # LO QUE SE MUESTRA ES LO QUE VIAJÓ A ARCA
 *
 * Todo sale del registro local del comprobante, **no de las fichas actuales**.
 * La condición de IVA del receptor es la que se declaró al emitir, aunque el
 * cliente hoy sea otra cosa: es lo que determinó la letra, y cambiarla
 * retroactivamente en pantalla haría parecer mal emitido algo que está bien.
 *
 * Tampoco se le pregunta nada a ARCA. Lo que ARCA tiene y nosotros no es
 * justamente lo que detecta la reconciliación (KAN-89); resolverlo acá taparía
 * el desfase en vez de mostrarlo.
 *
 * # SOLO LECTURA
 *
 * ⚠️ No hay ni va a haber un botón que escriba. Un comprobante fiscal no se
 * edita ni se borra: se corrige con una **nota de crédito**, que es una emisión
 * nueva y va por `POST /api/v1/comprobantes` como cualquier otra.
 */
export function DetalleDeComprobante() {
  // El id viene de la URL, así que puede ser cualquier cosa: `Number('abc')` es
  // `NaN`. `useComprobante` no dispara la query con un id no entero, y acá se
  // avisa en vez de dejar la pantalla colgada en "Buscando…".
  const { id } = useParams();
  const numero = Number(id);
  const esId = Number.isInteger(numero) && numero > 0;

  const consulta = useComprobante(numero);

  return (
    <main>
      <p>
        <Link to="/comprobantes">Volver al listado</Link>
      </p>

      {!esId ? (
        <p role="alert" className="error">
          «{id}» no es un id de comprobante. El id es el interno del comprobante, no su número
          (ese que se ve como 00001-00000015).
        </p>
      ) : null}

      {esId && consulta.isPending ? <p role="status">Buscando el comprobante…</p> : null}

      {consulta.isError ? (
        <p role="alert" className="error">
          {mensajeDeConsulta(consulta.error, 'detalle')}
        </p>
      ) : null}

      {consulta.data ? <Comprobante comprobante={consulta.data} /> : null}
    </main>
  );
}

function Comprobante({ comprobante }: { comprobante: ComprobanteDetalle }) {
  const c = comprobante;
  return (
    <>
      {/* La letra sale del campo `letra` que ya resolvió el backend. El front
          NUNCA la deduce: depende de la condición fiscal del emisor cruzada con
          la del receptor, y dos formas de averiguar lo mismo son dos formas de
          que un día no coincidan. */}
      <h1>
        {nombreDeComprobante(c.tipo, c.letra)} {numeroFormateado(c.puntoVenta, c.numero)}
      </h1>

      {/* El papel que se le manda al cliente (KAN-147). Va acá arriba y no al
          final: es lo que la mayoría viene a buscar cuando abre un detalle. */}
      <BotonesDePdf id={c.id} />

      <section aria-labelledby="autorizacion">
        <h2 id="autorizacion">Autorización de ARCA</h2>
        <dl>
          <dt>CAE</dt>
          <dd className="cae">{c.cae}</dd>
          <dt>Vencimiento del CAE</dt>
          <dd>{fechaArgentina(c.caeVencimiento)}</dd>
          <dt>Fecha de emisión</dt>
          <dd>{fechaArgentina(c.fecha)}</dd>
        </dl>
      </section>

      <section aria-labelledby="receptor">
        <h2 id="receptor">Receptor</h2>
        <p className="nota">
          Los datos con los que se emitió, no los de la ficha actual del cliente: son los que
          determinaron la letra.
        </p>
        <dl>
          <dt>Documento</dt>
          <dd>{receptorLegible(c.receptor.docTipo, c.receptor.docNro)}</dd>
          <dt>Condición frente al IVA</dt>
          <dd>{etiquetaDeCondicionIva(c.receptor.condicionIva)}</dd>
        </dl>
      </section>

      <section aria-labelledby="operacion">
        <h2 id="operacion">Operación</h2>
        <dl>
          <dt>Concepto</dt>
          <dd>{etiquetaDeConcepto(c.concepto)}</dd>
          {/* Solo con concepto 2 o 3: en productos ARCA no lleva período. */}
          {c.servicioDesde || c.servicioHasta ? (
            <>
              <dt>Período facturado</dt>
              <dd>
                {fechaArgentina(c.servicioDesde)} — {fechaArgentina(c.servicioHasta)}
              </dd>
            </>
          ) : null}
          {c.vtoPago ? (
            <>
              <dt>Vencimiento de pago</dt>
              <dd>{fechaArgentina(c.vtoPago)}</dd>
            </>
          ) : null}
          {c.comprobanteAsociadoNro != null ? (
            <>
              {/* Obligatorio en NC y ND por RG 4540/2019. Con el tipo y no solo
                  el número (KAN-193): una ND puede revertir una NC, y la
                  Factura A N° 3 y la NC A N° 3 conviven en el mismo punto de
                  venta. El asociado es siempre de la misma letra y punto de
                  venta que este comprobante. */}
              <dt>Comprobante asociado</dt>
              <dd>
                {c.comprobanteAsociadoTipo != null
                  ? `${nombreDeComprobante(c.comprobanteAsociadoTipo, c.letra)} ${numeroFormateado(c.puntoVenta, c.comprobanteAsociadoNro)}`
                  : `N° ${c.comprobanteAsociadoNro}`}
              </dd>
            </>
          ) : null}
          <dt>Moneda</dt>
          <dd>
            {c.moneda}
            {c.cotizacion !== 1 ? ` (cotización ${c.cotizacion})` : ''}
          </dd>
        </dl>
      </section>

      <section aria-labelledby="importes">
        <h2 id="importes">Importes</h2>
        <DesgloseIva renglones={c.desgloseIva} letra={c.letra} />
        <dl>
          <dt>Neto</dt>
          <dd className="importe">{formateaMoneda(c.neto)}</dd>
          <dt>IVA</dt>
          <dd className="importe">{formateaMoneda(c.iva)}</dd>
          <dt>Total</dt>
          <dd className="importe">
            <strong>{formateaMoneda(c.total)}</strong>
          </dd>
        </dl>
      </section>
    </>
  );
}

/**
 * Un renglón por alícuota.
 *
 * **Vacío no es un error**: en clase C no hay IVA que discriminar y ARCA
 * rechaza un array `Iva` en un comprobante C. Se dice con todas las letras en
 * vez de mostrar una tabla vacía, que se leería como un dato que falta.
 *
 * Los importes se muestran tal como vinieron y **no se suman acá**: el
 * redondeo se hizo por alícuota antes de sumar, del lado del backend, y ARCA
 * valida al centavo que `ImpTotal = ImpNeto + ImpIVA`. Recalcular el total en
 * JavaScript es la forma exacta de mostrar un centavo distinto al del papel.
 */
function DesgloseIva({ renglones, letra }: { renglones: readonly RenglonIva[]; letra: string }) {
  if (renglones.length === 0) {
    return (
      <p className="nota">
        Sin desglose de IVA: los comprobantes clase {letra} no discriminan IVA.
      </p>
    );
  }
  return (
    <table>
      <caption className="nota">
        El neto gravado a cada alícuota, tal como viajó en el array <code>Iva</code>.
      </caption>
      <thead>
        <tr>
          <th scope="col">Alícuota</th>
          <th scope="col">Neto gravado</th>
          <th scope="col">IVA</th>
        </tr>
      </thead>
      <tbody>
        {renglones.map((r) => (
          <tr key={r.alicuota}>
            {/* El porcentaje viene derivado del Id por el backend: ARCA nunca
                redefine la tasa de un Id existente. */}
            <td>{r.porcentaje}%</td>
            <td className="importe">{formateaMoneda(r.base)}</td>
            <td className="importe">{formateaMoneda(r.importe)}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}
